import * as React from 'react';
import { Search, X } from 'lucide-react';

export interface SearchGlobalProps {
  /**
   * Value of the search input
   */
  value?: string;
  /**
   * Callback when value changes
   */
  onChange?: (value: string) => void;
  /**
   * Callback when search is submitted
   */
  onSubmit?: (value: string) => void;
  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

/**
 * SearchGlobal component - Arbor Design System
 *
 * A search input that expands from 160px to 300px when focused.
 * Shows keyboard shortcut (⌘ K) on hover and X button when focused.
 */
export const SearchGlobal = React.forwardRef<HTMLInputElement, SearchGlobalProps>(
  (
    {
      value = '',
      onChange,
      onSubmit,
      onClear,
      placeholder = 'Search',
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit?.(value);
      }
    };

    const handleClear = () => {
      onChange?.('');
      onClear?.();
      inputRef.current?.focus();
    };

    const containerStyles: React.CSSProperties = {
      position: 'relative',
      width: isFocused ? '300px' : '160px',
      height: '32px',
      backgroundColor: isFocused ? '#ffffff' : isHovered ? '#efefef' : '#f8f8f8',
      borderRadius: '16px',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: isFocused ? '8px' : '8px',
      cursor: 'text',
      transition: 'all 0.2s ease-in-out',
      boxSizing: 'border-box',
      border: isFocused ? '1px solid #efefef' : 'none',
      boxShadow: isFocused ? '0px 0px 0px 3px #3cad51' : 'none',
      marginLeft: isFocused ? '-140px' : '0', // Expand to the left
      ...style,
    };

    const iconContainerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      padding: '2px',
    };

    const inputStyles: React.CSSProperties = {
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '13px',
      fontWeight: 400,
      color: '#2f2f2f',
      lineHeight: '1.5',
      flex: 1,
      width: '100%',
      minWidth: 0,
    };

    const keyboardShortcutStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '0px',
      flexShrink: 0,
    };

    const keyStyles: React.CSSProperties = {
      border: '1px solid #2f2f2f',
      borderRadius: '5px',
      padding: '0px 3px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Work Sans', sans-serif",
      fontSize: '8px',
      fontWeight: 400,
      color: '#2f2f2f',
      letterSpacing: '-0.08px',
      lineHeight: '1.5',
      minWidth: '16px',
    };

    const plusStyles: React.CSSProperties = {
      fontFamily: "'Work Sans', sans-serif",
      fontSize: '8px',
      fontWeight: 400,
      color: '#2f2f2f',
      letterSpacing: '-0.08px',
      lineHeight: '1.5',
      padding: '0 2px',
    };

    const clearButtonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '16px',
      height: '16px',
      cursor: 'pointer',
      border: 'none',
      background: 'none',
      padding: 0,
    };

    const showKeyboardShortcut = isHovered && !isFocused;
    const showClearButton = isFocused;

    return (
      <div
        className={className}
        style={containerStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => inputRef.current?.focus()}
        data-testid={dataTestId}
      >
        <div style={iconContainerStyles}>
          <Search size={12} color="#2f2f2f" strokeWidth={2} />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={isFocused ? '' : placeholder}
          style={inputStyles}
        />

        {showKeyboardShortcut && (
          <div style={keyboardShortcutStyles}>
            <div style={keyStyles}>⌘</div>
            <span style={plusStyles}>+</span>
            <div style={keyStyles}>K</div>
          </div>
        )}

        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            style={clearButtonStyles}
            aria-label="Clear search"
          >
            <X size={13.333} color="#2f2f2f" strokeWidth={2} />
          </button>
        )}
      </div>
    );
  }
);

SearchGlobal.displayName = 'SearchGlobal';
